import { useMemo } from 'react';

import { calculateCartSubTotal, estimateDeliveryFees } from 'features/cart/utils';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';
import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';

import { useCartContext } from '../../hooks/use-cart-context';
import { commonStyles } from '../../styles';
import { CartSectionHeader } from '../cart-section-header';

const Row = ({ label, value }: { label: string; value: string }) => {
  return (
    <View style={styles.row}>
      <Typography style={styles.rowLabel}>{label}</Typography>
      <Typography style={styles.rowValue}>{value}</Typography>
    </View>
  );
};

export const DeliveryFeesSection = () => {
  const { isDelivery } = useDerivedServiceModeContext();
  const { deliveryAddress } = useOrderContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { cartEntries } = useCartContext();

  const subTotalCents = useMemo(() => calculateCartSubTotal(cartEntries), [cartEntries]);

  const fees = useMemo(() => {
    return estimateDeliveryFees({
      subTotalCents,
      dropoffState: deliveryAddress?.state ?? null,
    });
  }, [deliveryAddress?.state, subTotalCents]);

  if (!isDelivery) {
    return null;
  }

  // Avoid showing a fees block for an empty cart.
  if (cartEntries.length === 0) {
    return null;
  }

  const deliveryFeeText = formatCurrencyForLocale(fees.deliveryFeeCents);
  const smallCartFeeText = formatCurrencyForLocale(fees.smallCartFeeCents);
  const geoFeeText = formatCurrencyForLocale(fees.geographicalFeeCents);
  const totalFeesText = formatCurrencyForLocale(fees.totalFeesCents);
  const serviceFeeText = formatCurrencyForLocale(fees.serviceFeeCents);

  return (
    <View style={styles.container} testID="delivery-fees-section">
      <CartSectionHeader title={t('deliveryFees')} testID="delivery-fees-section-heading-label" />

      <View style={styles.rows}>
        <Row label={t('deliveryFee')} value={deliveryFeeText} />
        {fees.serviceFeeCents > 0 && <Row label={t('serviceFee')} value={serviceFeeText} />}

        {fees.smallCartFeeCents > 0 && <Row label={t('smallCartFee')} value={smallCartFeeText} />}

        {fees.geographicalFeeCents > 0 && <Row label={t('geographicalFeeCO')} value={geoFeeText} />}

        <View style={styles.divider} />
        <Row label={t('deliveryFees')} value={totalFeesText} />
      </View>

      <Typography variant="headerThree" mt="$2" style={{ marginBottom: -2 }}>
        {t('disclaimer', 'Disclaimer')}
      </Typography>
      {region === Region.CA && (
        <Typography variant="bodyOne">{t('deliveryTerms.intro.ca.part1')}</Typography>
      )}
      {region === Region.US && (
        <Typography variant="bodyOne">{t('deliveryTerms.intro')}</Typography>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    overflow: 'hidden',
  },
  rows: {
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  rowLabel: {
    ...commonStyles.cartEntryBody,
    flex: 1,
  },
  rowValue: {
    ...commonStyles.cartEntryItemName,
  },
  divider: {
    height: 1,
    backgroundColor: primitive.$blackOpacity10,
    marginVertical: 4,
  },
});
